import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Flag from "@/components/Flag";
import EditableSubtitle from "@/components/EditableSubtitle";
import { useDateTimeFormat } from "@/lib/TzContext";
import { useI18n } from "@/lib/I18nContext";
import { teamLabel } from "@/lib/teamNames";
import { Lock, CheckCircle2, Info } from "lucide-react";

const KO_STAGES_IDS = ["r32", "r16", "qf", "sf", "third", "final"];

export default function Fase2Eliminatorias() {
  const { t } = useI18n();
  const [tab, setTab] = useState("r32");
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [preds, setPreds] = useState({});
  const [appState, setAppState] = useState({ predictions_published: false, stages: {} });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [m, t, mp, st] = await Promise.all([
        api.get("/tournament/matches"),
        api.get("/tournament/teams"),
        api.get("/predictions/me"),
        api.get("/app-state"),
      ]);
      setMatches(m.data);
      setTeams(t.data);
      setAppState(st.data);
      const map = {};
      (mp.data.matches || []).forEach((p) => { map[p.match_id] = p; });
      setPreds(map);
      setLoaded(true);
    })();
  }, []);

  const teamByCode = useMemo(() => Object.fromEntries(teams.map((t) => [t.code, t])), [teams]);
  const koMatches = useMemo(() => matches.filter((m) => m.stage !== "group"), [matches]);

  const stagePublished = (stageId) => !!(appState.stages && appState.stages[stageId]);

  const isLocked = (kickoffIso, stageId) => {
    if (stagePublished(stageId)) return true;
    const lock = new Date(kickoffIso).getTime() - 60 * 60 * 1000;
    return Date.now() >= lock;
  };

  // See Fase1Grupos.jsx: per-match save queue prevents network-order races
  // when the user types two fields rapidly.
  const saveQueuesRef = React.useRef({});

  const savePred = async (matchId, patch) => {
    let payload = null;
    setPreds((prev) => {
      const merged = { ...(prev[matchId] || {}), ...patch, match_id: matchId };
      payload = merged;
      return { ...prev, [matchId]: merged };
    });
    const prev = saveQueuesRef.current[matchId] || Promise.resolve();
    const next = prev.then(async () => {
      try {
        await api.put("/predictions/match", {
          match_id: matchId,
          home_score: payload.home_score ?? null,
          away_score: payload.away_score ?? null,
          winner_team: payload.winner_team ?? null,
        });
      } catch (e) {
        if (e?.response?.status === 423) toast.error(e.response.data?.detail || t("err.locked"));
        else if (e?.response?.status === 409) toast.error(e.response.data?.detail || t("phase2.tbd"));
        else toast.error(t("err.save"));
      }
    });
    saveQueuesRef.current[matchId] = next;
    await next;
    if (saveQueuesRef.current[matchId] === next) {
      delete saveQueuesRef.current[matchId];
    }
  };

  if (!loaded) return <div className="text-zinc-400">{t("common.loading")}</div>;

  const KO_STAGES = KO_STAGES_IDS.map((id) => ({
    id,
    label: t(`phase2.stages.${id}`),
    short: t(`phase2.short.${id}`),
  }));

  const stageMatches = koMatches
    .filter((m) => m.stage === tab)
    .sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff));
  const stageDefinedCount = stageMatches.filter((m) => m.home && m.away).length;
  const currentStage = KO_STAGES.find((s) => s.id === tab);
  const currentPublished = stagePublished(tab);

  return (
    <div className="space-y-6" data-testid="fase2-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("phase2.kicker")}</div>
        <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">{t("phase2.title")}</h1>
        <EditableSubtitle
          pageKey="fase2"
          fallback={t("phase2.subtitle.fallback")}
        />
      </div>

      {/* Stage Tabs */}
      <div className="border-b border-zinc-800 flex gap-1 overflow-x-auto" data-testid="fase2-tabs">
        {KO_STAGES.map((s) => {
          const published = stagePublished(s.id);
          return (
            <button
              key={s.id}
              data-testid={`fase2-tab-${s.id}`}
              onClick={() => setTab(s.id)}
              className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                tab === s.id
                  ? "text-white border-b-2 border-[var(--c-lime)]"
                  : "text-zinc-500 hover:text-white"
              }`}
            >
              <span>{s.short}</span>
              {published && <Lock size={11} className="text-[var(--c-yellow)]" />}
            </button>
          );
        })}
      </div>

      {/* Stage Status banners */}
      {currentPublished && (
        <div className="p-3 bg-[var(--c-yellow)]/10 border border-[var(--c-yellow)] text-[var(--c-yellow)] text-sm flex items-center gap-2" data-testid={`stage-published-banner-${tab}`}>
          <CheckCircle2 size={16} /> {t("phase2.publishedBanner", { stage: currentStage?.label || "" })}
        </div>
      )}
      <div className="p-3 bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm flex items-center gap-2" data-testid={`stage-progress-${tab}`}>
        <Info size={16} className="text-[var(--c-sky)]" />
        <span>{t("phase2.progress", { defined: stageDefinedCount, total: stageMatches.length, stage: currentStage?.label || "" })}</span>
      </div>

      {/* Stage Matches */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`bracket-stage-${tab}`}>
        {stageMatches.length === 0 ? (
          <div className="text-zinc-500 text-sm col-span-full">{t("phase2.noMatches")}</div>
        ) : (
          stageMatches.map((m) => (
            <KOMatchCard
              key={m.match_id}
              m={m}
              preds={preds}
              teamByCode={teamByCode}
              savePred={savePred}
              locked={isLocked(m.kickoff, tab)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function KOMatchCard({ m, preds, teamByCode, savePred, locked }) {
  const p = preds[m.match_id] || {};
  const fmt = useDateTimeFormat();
  const { t, lang } = useI18n();
  const teamsSet = !!m.home && !!m.away;
  const homeT = teamsSet ? teamByCode[m.home] : null;
  const awayT = teamsSet ? teamByCode[m.away] : null;
  const homeName = teamLabel(m.home, homeT?.name, lang);
  const awayName = teamLabel(m.away, awayT?.name, lang);
  const date = m.kickoff;
  const homeScore = p.home_score;
  const awayScore = p.away_score;
  const isDraw = homeScore != null && awayScore != null && homeScore === awayScore;
  // Auto-derive bracket winner from score (only if decisive); for draws the user can pick explicitly.
  const derivedWinner =
    homeScore != null && awayScore != null && homeScore !== awayScore
      ? (homeScore > awayScore ? m.home : m.away)
      : null;
  const bracketWinner = derivedWinner || p.winner_team;

  return (
    <div className="bg-zinc-900 border border-zinc-800 p-3" data-testid={`ko-card-${m.match_id}`}>
      <div className="text-[9px] font-mono-data text-zinc-500 flex items-center justify-between mb-2">
        <span>{m.match_id}</span>
        <span>{fmt.short(date)}</span>
      </div>
      {teamsSet ? (
        <>
          {/* Home team row */}
          <div
            className={`w-full flex items-center gap-2 px-2 py-2 mb-1 border ${
              bracketWinner === m.home ? "border-[var(--c-lime)]" : "border-zinc-800"
            }`}
            data-testid={`ko-row-home-${m.match_id}`}
          >
            <Flag code={m.home} flag={homeT?.flag} flagImageUrl={homeT?.flag_image_url} size={20} />
            <span className="text-xs flex-1 truncate" title={homeName}>{homeName}</span>
            <input
              type="number" min={0} max={20}
              disabled={locked}
              value={homeScore ?? ""}
              onChange={(e) => savePred(m.match_id, { home_score: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              className="score-input w-10 h-8 text-sm"
              data-testid={`ko-home-score-${m.match_id}`}
            />
          </div>
          {/* Away team row */}
          <div
            className={`w-full flex items-center gap-2 px-2 py-2 border ${
              bracketWinner === m.away ? "border-[var(--c-lime)]" : "border-zinc-800"
            }`}
            data-testid={`ko-row-away-${m.match_id}`}
          >
            <Flag code={m.away} flag={awayT?.flag} flagImageUrl={awayT?.flag_image_url} size={20} />
            <span className="text-xs flex-1 truncate" title={awayName}>{awayName}</span>
            <input
              type="number" min={0} max={20}
              disabled={locked}
              value={awayScore ?? ""}
              onChange={(e) => savePred(m.match_id, { away_score: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              className="score-input w-10 h-8 text-sm"
              data-testid={`ko-away-score-${m.match_id}`}
            />
          </div>

          {/* Draw → ask who advances on penalties (only for bracket display) */}
          {isDraw && (
            <div className="mt-2 p-2 border border-dashed border-[var(--c-yellow)] bg-[var(--c-yellow)]/5">
              <div className="text-[10px] uppercase tracking-widest text-[var(--c-yellow)] font-mono-data mb-1">
                {t("phase2.penalties.title")}
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => savePred(m.match_id, { winner_team: m.home })}
                  data-testid={`ko-pen-home-${m.match_id}`}
                  className={`flex-1 text-[10px] px-2 py-1 border ${
                    p.winner_team === m.home ? "bg-[var(--c-yellow)] text-black border-[var(--c-yellow)] font-bold" : "border-zinc-800 hover:border-white"
                  } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {m.home}
                </button>
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => savePred(m.match_id, { winner_team: m.away })}
                  data-testid={`ko-pen-away-${m.match_id}`}
                  className={`flex-1 text-[10px] px-2 py-1 border ${
                    p.winner_team === m.away ? "bg-[var(--c-yellow)] text-black border-[var(--c-yellow)] font-bold" : "border-zinc-800 hover:border-white"
                  } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {m.away}
                </button>
              </div>
              <div className="text-[9px] text-zinc-500 mt-1 leading-tight">
                {t("phase2.penalties.help")}
              </div>
            </div>
          )}

          {locked && (
            <div className="mt-2 flex items-center gap-1 text-[10px] text-[var(--c-red)] font-mono-data">
              <Lock size={10} /> {t("phase2.locked")}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="w-full px-2 py-2 mb-1 border border-dashed border-zinc-800 bg-zinc-950 text-zinc-600 text-xs font-mono-data flex items-center justify-center">
            <span>{t("phase2.tbd")}</span>
          </div>
          <div className="w-full px-2 py-2 border border-dashed border-zinc-800 bg-zinc-950 text-zinc-600 text-xs font-mono-data flex items-center justify-center">
            <span>{t("phase2.tbd")}</span>
          </div>
        </>
      )}
    </div>
  );
}
